import dynamic from 'next/dynamic'
import React, { Component, ErrorInfo, ReactNode } from 'react'
import {
  ILoggerContext,
  LoggerContext
} from 'src/context/LoggerProvider/useLogger'
import { isStaleChunkError } from 'src/utils/clientErrorNoise'
import { handleStaleChunkError } from 'src/utils/staleChunkReload'

const ClientError = dynamic(() => import('../screens/Errors/ClientError'))

interface Props {
  children?: ReactNode
  /**
   * Rendered instead of the full-page `<ClientError />` screen. Pass this
   * whenever the boundary wraps a SINGLE feature rather than the whole app: a
   * third-party widget that throws mid-commit should degrade to its own
   * placeholder, not replace the page the user was actually using.
   */
  fallback?: ReactNode
  /**
   * Which boundary caught the error, forwarded to Datadog so a scoped failure
   * is distinguishable from an app-root one at triage time. Defaults to `app`
   * (the `_app.tsx` boundary); scoped boundaries pass their surface name.
   */
  boundary?: string
}

interface State {
  hasError: boolean
  error?: Error | undefined
}

class ErrorBoundary extends Component<Props, State> {
  static contextType = LoggerContext

  public state: State = {
    hasError: false
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // A lazy chunk that 404s after a deploy lands HERE, not on window.onerror.
    // Reload once instead of showing the error screen (staleChunkReload.ts).
    if (isStaleChunkError(`${error?.name} ${error?.message}`)) {
      handleStaleChunkError()
    }
    const logger = this.context as ILoggerContext
    logger?.error(
      'Uncaught Error',
      { errorInfo, boundary: this.props.boundary ?? 'app' },
      error
    )
  }

  public render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <ClientError />
    }

    return this.props.children
  }
}

export default React.memo(ErrorBoundary)
